/**
 * VoyageTemplateBuilder.js
 *
 * Full-screen form for creating/editing voyage templates
 *
 * PHASE 3: Voyage Template Builder
 */

import React, { useState, useCallback } from 'react';
import {
  ArrowLeft,
  MapPin,
  Flag,
  Settings,
  Plus,
  Trash2
} from 'lucide-react';

// Config
import { VOYAGE_PURPOSE_LABELS } from '../../../../config/operationsConfig';

const VoyageTemplateBuilder = ({
  initialData = null,
  onSave,
  onCancel
}) => {
  const isEditing = !!initialData;

  // Form state
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [from, setFrom] = useState(initialData?.from?.name || '');
  const [to, setTo] = useState(initialData?.to?.name || '');
  const [purpose, setPurpose] = useState(initialData?.purpose || '');
  const [reportingPoints, setReportingPoints] = useState(initialData?.reportingPoints || []);
  const [expectedDuration, setExpectedDuration] = useState(initialData?.defaults?.expectedDurationHours || '');
  const [typicalCrew, setTypicalCrew] = useState(initialData?.defaults?.typicalCrewCount || '');
  const [typicalPassengers, setTypicalPassengers] = useState(initialData?.defaults?.typicalPassengerCount || '');
  const [notes, setNotes] = useState(initialData?.defaults?.notes || '');

  // Validation
  const isValid = name.trim() && from.trim() && to.trim();

  // Add reporting point
  const addReportingPoint = useCallback(() => {
    setReportingPoints(prev => [...prev, { name: '', notes: '' }]);
  }, []);

  // Update reporting point
  const updateReportingPoint = useCallback((index, field, value) => {
    setReportingPoints(prev => prev.map((rp, i) =>
      i === index ? { ...rp, [field]: value } : rp
    ));
  }, []);

  // Remove reporting point
  const removeReportingPoint = useCallback((index) => {
    setReportingPoints(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    if (!isValid) return;

    const templateData = {
      name: name.trim(),
      description: description.trim(),
      from: { name: from.trim() },
      to: { name: to.trim() },
      purpose,
      checklists: [],
      reportingPoints: reportingPoints.filter(rp => rp.name.trim()),
      defaults: {
        expectedDurationHours: expectedDuration ? parseInt(expectedDuration, 10) : null,
        typicalCrewCount: typicalCrew ? parseInt(typicalCrew, 10) : null,
        typicalPassengerCount: typicalPassengers ? parseInt(typicalPassengers, 10) : null,
        passengerBriefRequired: typicalPassengers > 0,
        notes: notes.trim()
      }
    };

    onSave(templateData);
  }, [name, description, from, to, purpose, reportingPoints,
      expectedDuration, typicalCrew, typicalPassengers, notes, isValid, onSave]);

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={onCancel} className="p-2 -ml-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Template' : 'New Template'}
            </h1>
          </div>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className={`px-4 py-2 rounded-lg font-medium ${
              isValid
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Save
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto p-4 pb-24 space-y-6">

        {/* Basic Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-400" />
            Template Info
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Hull → Hornsea CTV Transfer"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this voyage type"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
        </div>

        {/* Route */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-gray-400" />
            Route
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
              <input
                type="text"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="Departure port"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
              <input
                type="text"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="Destination"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
            <select
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="">Select purpose</option>
              {Object.entries(VOYAGE_PURPOSE_LABELS || {}).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Reporting Points */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Flag className="h-5 w-5 text-gray-400" />
              Reporting Points
            </h2>
            <button
              onClick={addReportingPoint}
              className="text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          <p className="text-sm text-gray-500">
            Text reminders that appear in the voyage log as prompts
          </p>

          {reportingPoints.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No reporting points added</p>
          ) : (
            <div className="space-y-3">
              {reportingPoints.map((rp, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={rp.name}
                      onChange={(e) => updateReportingPoint(index, 'name', e.target.value)}
                      placeholder="Point name (e.g., Spurn Point)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500"
                    />
                    <input
                      type="text"
                      value={rp.notes}
                      onChange={(e) => updateReportingPoint(index, 'notes', e.target.value)}
                      placeholder="Notes (e.g., Report to VTS on Ch 14)"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <button
                    onClick={() => removeReportingPoint(index)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Defaults */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <h2 className="font-semibold text-gray-900">Defaults</h2>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hrs)</label>
              <input
                type="number"
                value={expectedDuration}
                onChange={(e) => setExpectedDuration(e.target.value)}
                placeholder="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Crew</label>
              <input
                type="number"
                value={typicalCrew}
                onChange={(e) => setTypicalCrew(e.target.value)}
                placeholder="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Passengers</label>
              <input
                type="number"
                value={typicalPassengers}
                onChange={(e) => setTypicalPassengers(e.target.value)}
                placeholder="12"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any standing notes for this voyage type"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default VoyageTemplateBuilder;
